import React, { useState, useEffect } from 'react';
import { Chip, Stack, Typography, Button, IconButton } from '@mui/material';
import { CheckmarkCirclePlusOutline, Plus, TrashcanOutline, XCircleFilled } from '@deepup/icons';
import { OrderNumber, isOrderNumberClosed } from '@projects/types/orderNumbers';
import { errorBox, infoBox, successBox } from '@core/components/AlertBox';
import { Scan } from '@scans/types/scan';
import { setSelectedScan } from '@scans/components/Scans/state';
import AddSMADialog from './AddSMADialog';
import { useSaveSMA } from './hooks';

interface SMADetailsProps {
  projectId: string | undefined;
  photoId: string | undefined;
  scanId: string | undefined;
  smaNumbers: OrderNumber[] | null;
}

export const SMADetails: React.FC<SMADetailsProps> = ({
  projectId,
  photoId,
  scanId,
  smaNumbers,
}) => {
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [displayedSMANumbers, setDisplayedSMANumbers] = useState<OrderNumber[]>([]);
  const [selectedSMAs, setSelectedSMAs] = useState<OrderNumber[]>([]);
  const [showSaveCancel, setShowSaveCancel] = useState<boolean>(false);
  const [smasToDelete, setSmasToDelete] = useState<string[]>([]);

  const isPhoto = Boolean(photoId);
  const entityId = isPhoto ? photoId : scanId;
  const entityType = isPhoto ? 'photos' : 'scans';
  const { saveSMA } = useSaveSMA({
    projectId: projectId ?? '',
    onSuccess: (updatedEntity) => {
      if (entityType === 'scans') {
        setSelectedScan(updatedEntity as Scan);
      }
    },
  });
  useEffect(() => {
    setDisplayedSMANumbers(smaNumbers ?? []);
  }, [smaNumbers]);

  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
  };

  const handleNewSMA = (newSMA: OrderNumber) => {
    if (
      displayedSMANumbers.some((sma) => sma.value === newSMA.value) ||
      selectedSMAs.some((sma) => sma.value === newSMA.value)
    ) {
      infoBox(`${newSMA.value} already present in SMA list.`);
      return;
    }

    setSelectedSMAs((prev) => [...prev, newSMA]);
    setShowSaveCancel(true);
  };

  const handleRemoveSelectedSMA = (smaToRemove: OrderNumber) => {
    setSelectedSMAs((prev) => prev.filter((sma) => sma.value !== smaToRemove.value));
    if (selectedSMAs.length === 1) {
      setShowSaveCancel(false);
    }
  };

  const handleSaveChanges = async () => {
    const updatedOrderNumbers = [...displayedSMANumbers, ...selectedSMAs];
    try {
      await saveSMA({
        orderNumbers: updatedOrderNumbers,
        entityId: entityId ?? '',
        entityType,
      });

      setDisplayedSMANumbers(updatedOrderNumbers);
      setSelectedSMAs([]);
      const textSmaList = updatedOrderNumbers.map((sma) => sma.value).join(', ');
      const msg = `SMA numbers ${textSmaList} assigned successfully.`;
      successBox(msg);
      setShowSaveCancel(false);
    } catch (error) {
      errorBox(`Failed to save changes: ${error}`);
    }
  };

  const handleCancelChanges = () => {
    setSelectedSMAs([]);
    setShowSaveCancel(false);
  };

  const handleMarkForDeletion = (smaValue: string) => {
    if (showSaveCancel) {
      return;
    }
    setSmasToDelete((prev) =>
      prev.includes(smaValue) ? prev.filter((sma) => sma !== smaValue) : [...prev, smaValue],
    );
  };

  const handleDeleteMarkedSMAs = async () => {
    const updatedOrderNumbers = displayedSMANumbers.filter(
      (sma) => !smasToDelete.includes(sma.value),
    );
    try {
      await saveSMA({
        orderNumbers: updatedOrderNumbers,
        entityId: entityId ?? '',
        entityType,
      });

      const textSmaList = smasToDelete.join(', ');
      const msg = `SMA numbers ${textSmaList} deleted successfully.`;
      successBox(msg);
      setDisplayedSMANumbers(updatedOrderNumbers);
      setSmasToDelete([]);
    } catch (error) {
      errorBox(`Failed to delete SMAs: ${error}`);
    }
  };

  const handleCancelDeletion = () => {
    setSmasToDelete([]);
  };

  return (
    <Stack p={2} spacing={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="overline">SMA</Typography>
        <IconButton color="default" size="medium" onClick={handleOpenDialog}>
          <Plus />
        </IconButton>
      </Stack>

      <Stack direction="row" flexWrap="wrap" gap={1}>
        {displayedSMANumbers.length > 0 ? (
          displayedSMANumbers.map((smaInfo, index) => {
            const closed = isOrderNumberClosed(smaInfo);
            return (
              <Chip
                key={`${smaInfo.value}-${index}`}
                label={`${smaInfo.value}${closed ? ' (Closed)' : ''}`}
                onDelete={closed ? undefined : () => handleMarkForDeletion(smaInfo.value)}
                deleteIcon={<XCircleFilled />}
                variant="outlined"
                sx={(theme) => ({
                  borderColor: smasToDelete.includes(smaInfo.value)
                    ? theme.palette.error.main
                    : closed
                      ? theme.palette.text.disabled
                      : theme.palette.divider,
                  color: smasToDelete.includes(smaInfo.value)
                    ? theme.palette.error.main
                    : closed
                      ? theme.palette.text.disabled
                      : theme.palette.text.primary,
                  backgroundColor: closed ? theme.palette.action.hover : 'transparent',
                  opacity: closed ? 0.7 : 1,
                  '& .MuiChip-deleteIcon': {
                    color: smasToDelete.includes(smaInfo.value)
                      ? theme.palette.error.main
                      : closed
                        ? theme.palette.text.disabled
                        : theme.palette.text.secondary,
                  },
                })}
              />
            );
          })
        ) : (
          <Typography variant="body2" color="textSecondary">
            No SMA numbers selected.
          </Typography>
        )}
      </Stack>

      {selectedSMAs.length > 0 && (
        <>
          <Typography variant="subtitle2">Pending SMAs:</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {selectedSMAs.map((sma, index) => (
              <Chip
                key={`pending-${sma.value}-${index}`}
                label={sma.value}
                onDelete={() => handleRemoveSelectedSMA(sma)}
                color="success"
                variant="outlined"
                sx={(theme) => ({
                  borderColor: theme.palette.success.main,
                  color: theme.palette.success.main,
                  '& .MuiChip-deleteIcon': {
                    color: theme.palette.success.main,
                  },
                })}
              />
            ))}
          </Stack>
        </>
      )}

      {smasToDelete.length > 0 && (
        <Stack direction="row" justifyContent="flex-end" padding={1} spacing={2}>
          <Button onClick={handleCancelDeletion} color="secondary" variant="outlined">
            CANCEL
          </Button>
          <Button
            onClick={handleDeleteMarkedSMAs}
            startIcon={<TrashcanOutline />}
            color="error"
            variant="contained"
            disabled={smasToDelete.length === 0}
          >
            DELETE
          </Button>
        </Stack>
      )}

      {showSaveCancel && (
        <Stack direction="row" justifyContent="flex-end" padding={1} spacing={2}>
          <Button onClick={handleCancelChanges} color="secondary" variant="outlined">
            CANCEL
          </Button>
          <Button
            onClick={handleSaveChanges}
            startIcon={<CheckmarkCirclePlusOutline />}
            color="primary"
            variant="contained"
          >
            SAVE CHANGES
          </Button>
        </Stack>
      )}

      {isDialogOpen && (
        <AddSMADialog
          open={isDialogOpen}
          onClose={handleCloseDialog}
          onAddSMA={handleNewSMA}
          selectedSMAs={selectedSMAs}
          onRemoveSelectedSMA={handleRemoveSelectedSMA}
          entityId={entityId}
        />
      )}
    </Stack>
  );
};
